import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { z } from 'zod';
import {
  AdminCloneArtifactsResponseSchema,
  AdminCloneUserResponseSchema,
  AdminProductionUsersResponseSchema,
} from '@shared/schemas/admin-ops';
import { AdminUsersResponseSchema } from '@shared/schemas/admin-users-teams';
import { DualWriteStatsSchema } from '@shared/dual-write/stats.schema';
import {
  BlueprintResetAllResponseSchema,
  BlueprintResetNuclearResponseSchema,
  BlueprintResetResponseSchema,
  ReplayResponseSchema,
  SeedStatusResponseSchema,
  StoryArcsListResponseSchema,
} from '@shared/blueprints/admin-wire';
import { logger } from './lib/logger';
import { errorMessageFromResponse, useApi } from './lib/api';
import { ApiProblemError, fetchJson, type ApiFetch } from './lib/apiFetchJson';
import { fetchJsonOnHttp } from './lib/fetchJsonOnHttp';
import { Button } from './components/ui/Button';
import { typography } from './theme';
import { fetchAdminMe } from './api/admin';
import { Search } from 'lucide-react';
import { UserMultiSelect, AdminUser } from './components/admin/shared/UserMultiSelect';
import { consumeSSE } from './components/admin/shared/consumeSSE';

// ── Types ──
interface StoryArcInfo {
  id: string;
  displayName: string;
  description: string;
  accountName: string;
  theme: string;
  beatCount: number;
  blueprintKinds: string[];
  active: boolean;
  seeded: number;
  total: number;
}

interface FillStatus {
  seeded: number;
  total: number;
}

interface SeedStatus {
  blueprints: boolean;
  fill: FillStatus;
  legacy: boolean;
}

/** These calls run a server-side sequence of deletes or copies that keeps going
 *  after the client gives up, so they get far more than `apiFetch`'s default. */
const LONG_RUNNING_MS = 120_000;

/** The GET fallbacks below: a non-2xx has always meant "show the empty state". */
const nullOnHttp = () => null;

/** True when the request ended without an answer (cut off or never reached),
 *  so the operation may still be running on the server. */
function endedWithoutAnswer(err: unknown): boolean {
  return err instanceof ApiProblemError && (err.kind === 'aborted' || err.kind === 'network');
}

const MAY_STILL_BE_RUNNING = 'The connection ended before Terra answered. The operation may still be running. Refresh before retrying.';

interface ResetAction {
  key: 'v4' | 'all' | 'nuclear';
  label: string;
  description: string;
  failureLabel: string;
  /** POSTs the reset and returns the success banner text. */
  run: (apiFetch: ApiFetch) => Promise<string>;
}

function resetAction<S extends z.ZodType>(def: {
  key: ResetAction['key'];
  label: string;
  description: string;
  endpoint: string;
  failureLabel: string;
  schema: S;
  describe: (result: z.output<S>) => string;
}): ResetAction {
  const { endpoint, schema, describe, ...rest } = def;
  return {
    ...rest,
    run: async (apiFetch) =>
      describe(await fetchJson(apiFetch, schema, endpoint, { method: 'POST', timeoutMs: LONG_RUNNING_MS })),
  };
}

const resetActions: ResetAction[] = [
  resetAction({
    key: 'v4',
    label: 'Reset V4 Only',
    description: 'Deletes V4 blueprints and artifacts. Legacy records (terra_ingestion) are kept.',
    endpoint: '/api/v4/admin/blueprints/reset',
    failureLabel: 'Reset',
    schema: BlueprintResetResponseSchema,
    describe: () => 'V4 reset complete.',
  }),
  resetAction({
    key: 'all',
    label: 'Reset V4 + Executions',
    description: 'Deletes V4 blueprints, artifacts, and action executions. Legacy records are kept.',
    endpoint: '/api/v4/admin/blueprints/reset-all',
    failureLabel: 'Reset all',
    schema: BlueprintResetAllResponseSchema,
    describe: ({ deleted: d }) =>
      `Reset complete: ${d.artifacts} artifacts, ${d.blueprints} blueprints, ${d.executions} executions deleted`,
  }),
  resetAction({
    key: 'nuclear',
    label: 'Nuclear Reset',
    description: 'Deletes everything: legacy records, V4 artifacts, blueprints, and executions. Complete clean slate.',
    endpoint: '/api/v4/admin/blueprints/reset-nuclear',
    failureLabel: 'Nuclear reset',
    schema: BlueprintResetNuclearResponseSchema,
    describe: ({ deleted: d }) =>
      `Nuclear reset complete: ${d.legacy} legacy records, ${d.artifacts} artifacts, ${d.blueprints} blueprints, ${d.executions} executions deleted`,
  }),
];

/** What a rep sees when a clone call fails: the server's own `detail`/`title`
 *  when it sent one, else the shared fallback sentence. */
function describeAdminFailure(err: unknown): string {
  if (endedWithoutAnswer(err)) return MAY_STILL_BE_RUNNING;
  if (err instanceof ApiProblemError) return err.detail ?? err.title ?? err.message;
  return err instanceof Error ? err.message : String(err);
}

function DestructiveActionRow({
  action,
  isConfirming,
  isResetting,
  onRequestConfirm,
  onCancel,
  onConfirm,
}: {
  action: ResetAction;
  isConfirming: boolean;
  isResetting: boolean;
  onRequestConfirm: () => void;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="flex items-center justify-between">
      <div className="min-w-0">
        <span className="text-sm font-medium text-red-700">{action.label}</span>
        <p className="text-xs text-red-500 mt-0.5">{action.description}</p>
      </div>
      {isConfirming ? (
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="ghost" onClick={onConfirm} className="!text-red-700 !bg-red-100 !border-red-300 !border text-xs">
            Confirm
          </Button>
          <Button variant="ghost" onClick={onCancel} className="text-xs">
            Cancel
          </Button>
        </div>
      ) : (
        <Button
          variant="ghost"
          onClick={onRequestConfirm}
          disabled={isResetting}
          className="!text-red-600 hover:!bg-red-100 !border-red-200 !border shrink-0"
        >
          {isResetting ? 'Resetting...' : 'Reset'}
        </Button>
      )}
    </div>
  );
}

// ── Toggle switch component (shared between seed categories and story arcs) ──
function ToggleSwitch({
  active,
  disabled,
  onClick,
}: {
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`shrink-0 relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
        active ? 'bg-indigo-500' : 'bg-slate-300'
      } ${disabled ? 'opacity-50' : 'cursor-pointer'}`}
    >
      <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform ${
        active ? 'translate-x-4.5' : 'translate-x-0.5'
      }`} />
    </button>
  );
}

function formatProgressEvent(event: any): string {
  const { step, status, user, userIndex, userTotal, count, message } = event;
  if (step === 'blueprints' && status === 'started') return '... Seeding blueprints';
  if (step === 'blueprints' && status === 'done') return `Done: ${count} blueprints seeded`;
  if (step === 'artifacts' && status === 'started') return `... Seeding artifacts for ${user} (${userIndex}/${userTotal})`;
  if (step === 'artifacts' && status === 'done') return `Done: ${count} artifacts for ${user}`;
  if (step === 'arcs' && status === 'started') return `... Seeding arcs for ${user} (${userIndex}/${userTotal})`;
  if (step === 'arcs' && status === 'done') return `Done: ${count} arc artifacts for ${user}`;
  if (step === 'legacy' && status === 'started') return '... Seeding legacy templates';
  if (step === 'legacy' && status === 'done') return `Done: ${count} legacy templates seeded`;
  if (step === 'complete') return 'All done!';
  if (step === 'error') return `Error: ${message}`;
  return JSON.stringify(event);
}

// ── Backfill Section ──

interface CoverageStats {
  totalLegacy: number;
  bridgedCount: number;
  coveragePercent: number;
}

interface BackfillEvent {
  type: 'progress' | 'complete' | 'aborted' | 'error';
  processed?: number;
  total?: number;
  created?: number;
  updated?: number;
  skipped?: number;
  errors?: number;
  durationMs?: number;
  message?: string;
}

function BackfillSection({ apiFetch }: { apiFetch: ApiFetch }) {
  const queryClient = useQueryClient();
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<BackfillEvent | null>(null);
  const [lastResult, setLastResult] = useState<BackfillEvent | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const [confirmText, setConfirmText] = useState('');

  const coverageQuery = useQuery<CoverageStats>({
    queryKey: ['backfill-coverage'],
    queryFn: async () => {
      const data = await fetchJson(apiFetch, DualWriteStatsSchema, '/api/v4/admin/dual-write/stats');
      return data.coverage;
    },
  });

  const coverage = coverageQuery.data;

  const handleBackfill = useCallback(async () => {
    setRunning(true);
    setProgress(null);
    setLastResult(null);
    setConfirmText('');
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const resp = await apiFetch('/api/v4/admin/dual-write/backfill', {
        method: 'POST',
        signal: controller.signal,
      });
      if (!resp.ok) {
        const fallback = `HTTP ${resp.status}`;
        setLastResult({ type: 'error', message: await errorMessageFromResponse(resp, fallback) });
        return;
      }

      await consumeSSE(resp, (raw) => {
        const event = raw as unknown as BackfillEvent;
        if (event.type === 'progress') {
          setProgress(event);
        } else if (event.type === 'complete' || event.type === 'aborted') {
          setLastResult(event);
        } else if (event.type === 'error') {
          setLastResult(event);
        }
      });

      // Refresh coverage stats
      await queryClient.invalidateQueries({ queryKey: ['backfill-coverage'] });
    } catch (err) {
      setLastResult({ type: 'error', message: (err as Error).message });
    } finally {
      abortRef.current = null;
      setRunning(false);
    }
  }, [apiFetch, queryClient]);

  const pct = coverage
    ? coverage.totalLegacy > 0 ? coverage.coveragePercent : 100
    : 0;

  const isComplete = coverage?.totalLegacy === coverage?.bridgedCount && (coverage?.totalLegacy ?? 0) > 0;
  const canStart = !running && !isComplete && confirmText === 'BACKFILL';
  const currentProgress = running ? progress : null;
  const unbridgedCount = coverage ? coverage.totalLegacy - coverage.bridgedCount : 0;

  return (
    <div className="rounded-lg border border-red-200 bg-red-50/50 p-5 space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-red-700">Legacy → V4 Backfill</h3>
        <p className="text-xs text-red-500 mt-0.5">
          Writes V4 artifacts for all unbridged legacy records. Runs against live data using each record's original owner.
          This cannot be undone without a database reset.
        </p>
      </div>

      {/* Coverage bar */}
      {coverage && (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-xs">
            <span className="text-red-600">
              Coverage: {coverage.bridgedCount.toLocaleString()} / {coverage.totalLegacy.toLocaleString()} ({pct}%)
              {unbridgedCount > 0 && ` — ${unbridgedCount.toLocaleString()} rows to backfill`}
            </span>
            {isComplete && (
              <span className="text-content-success font-medium">Complete</span>
            )}
          </div>
          <div className="h-2 rounded-full bg-red-100 overflow-hidden">
            <div
              className="h-full rounded-full bg-red-400 transition-all duration-500"
              style={{ width: `${Math.min(100, pct)}%` }}
            />
          </div>
        </div>
      )}

      {/* Progress during backfill */}
      {currentProgress && (
        <div className="rounded-lg border border-red-200 bg-white p-3 space-y-2">
          <div className="flex items-center justify-between text-xs">
            <span className="text-content-secondary font-mono">
              Processing: {currentProgress.processed?.toLocaleString()} / {currentProgress.total?.toLocaleString()}
            </span>
            <span className="text-content-tertiary">
              {currentProgress.created} created, {currentProgress.updated} updated, {currentProgress.skipped} skipped
              {(currentProgress.errors ?? 0) > 0 && (
                <span className="text-content-danger">, {currentProgress.errors} errors</span>
              )}
            </span>
          </div>
          <div className="h-1.5 rounded-full bg-red-100 overflow-hidden">
            <div
              className="h-full rounded-full bg-red-400 transition-all"
              style={{ width: `${currentProgress.total ? (currentProgress.processed! / currentProgress.total) * 100 : 0}%` }}
            />
          </div>
        </div>
      )}

      {/* Result message */}
      {lastResult && !running && (
        <div className={`rounded-lg border px-3 py-2 text-xs ${
          lastResult.type === 'complete'
            ? 'bg-matcha-50 border-matcha-200 text-matcha-700'
            : lastResult.type === 'aborted'
              ? 'bg-lemon-50 border-lemon-200 text-lemon-700'
              : 'bg-pomegranate-50 border-pomegranate-200 text-pomegranate-700'
        }`}>
          {lastResult.type === 'complete' || lastResult.type === 'aborted' ? (
            <span>
              Backfill {lastResult.type === 'aborted' ? 'cancelled' : 'complete'} in {((lastResult.durationMs ?? 0) / 1000).toFixed(1)}s —{' '}
              {lastResult.created} created, {lastResult.updated} updated, {lastResult.skipped} skipped
              {(lastResult.errors ?? 0) > 0 && `, ${lastResult.errors} errors`}
            </span>
          ) : (
            <span>Backfill failed: {lastResult.message}</span>
          )}
        </div>
      )}

      {/* Type-to-confirm + action buttons */}
      {!running && !isComplete && (
        <div className="space-y-2">
          <label className="block text-xs text-red-600 font-medium">
            Type <span className="font-mono bg-red-100 px-1 py-0.5 rounded">BACKFILL</span> to confirm
          </label>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="BACKFILL"
            className="w-48 rounded-lg border border-red-300 bg-white px-3 py-1.5 text-sm font-mono text-red-700 placeholder:text-red-300 focus:outline-none focus:ring-2 focus:ring-red-200"
          />
        </div>
      )}

      <div className="flex justify-end gap-2">
        {running && (
          <Button
            onClick={() => abortRef.current?.abort()}
            variant="neutral"
          >
            Cancel
          </Button>
        )}
        {!isComplete && (
          <Button
            onClick={() => { void handleBackfill(); }}
            disabled={!canStart}
            className={canStart ? '!bg-red-600 !text-white !border-red-600 hover:!bg-red-700' : ''}
          >
            {running ? 'Running...' : 'Start Backfill'}
          </Button>
        )}
      </div>
    </div>
  );
}

// ── Clone User from Production ──

const CLONE_USER_TYPES = ['MEMBER', 'PRE_SALES', 'POST_SALES', 'REVOPS'] as const;

interface ProdUser {
  slackUserId: string;
  ownerEmail: string | null;
  userType: string | null;
}

function displayNameFromEmail(email: string): string {
  const local = email.split('@')[0] ?? email;
  return local
    .split('.')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function CloneUserSection({ apiFetch }: { apiFetch: ApiFetch }) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const prodUsersQuery = useQuery<ProdUser[]>({
    queryKey: ['production-users'],
    queryFn: async () => {
      const data = await fetchJsonOnHttp(apiFetch, AdminProductionUsersResponseSchema, '/api/admin/production-users', undefined, nullOnHttp);
      return data?.users ?? [];
    },
  });

  const prodUsers = useMemo(() => prodUsersQuery.data ?? [], [prodUsersQuery.data]);

  const filtered = useMemo(() => {
    let users = prodUsers;
    if (typeFilter) users = users.filter((u) => u.userType === typeFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      users = users.filter(
        (u) =>
          (u.ownerEmail?.toLowerCase().includes(q)) ||
          (u.ownerEmail && displayNameFromEmail(u.ownerEmail).toLowerCase().includes(q)) ||
          u.slackUserId.toLowerCase().includes(q),
      );
    }
    return users;
  }, [prodUsers, typeFilter, search]);

  const toggleUser = (slackUserId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(slackUserId)) next.delete(slackUserId);
      else next.add(slackUserId);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(filtered.map((u) => u.slackUserId)));
  const clearSelection = () => setSelected(new Set());

  const handleClone = useCallback(async () => {
    if (selected.size === 0) return;
    setLoading(true);
    setResult(null);
    try {
      const data = await fetchJson(apiFetch, AdminCloneUserResponseSchema, '/api/admin/clone-user', {
        method: 'POST',
        timeoutMs: LONG_RUNNING_MS,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slackUserIds: [...selected] }),
      });
      setResult({ type: 'success', text: `Cloned ${data.cloned.length} user${data.cloned.length !== 1 ? 's' : ''} from production` });
      setSelected(new Set());
      void queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    } catch (err) {
      setResult({ type: 'error', text: describeAdminFailure(err) });
    } finally {
      setLoading(false);
    }
  }, [selected, apiFetch, queryClient]);

  return (
    <div className="rounded-xl border border-nightshade-200 bg-white shadow-sm p-5 space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-nightshade-800">Clone Users from Production</h3>
        <p className="text-xs text-nightshade-500 mt-0.5">
          Copy user records from production so you can impersonate them on staging.
        </p>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-nightshade-400" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search users..."
          className="w-full pl-9 pr-3 py-1.5 text-sm rounded-lg border border-nightshade-200 bg-nightshade-50 focus:bg-white focus:border-blueberry-300 focus:ring-2 focus:ring-blueberry-100 outline-none transition-all placeholder:text-nightshade-400"
        />
      </div>

      {/* Type filter pills */}
      <div className="flex items-center gap-1.5">
        {CLONE_USER_TYPES.map((type) => {
          const count = prodUsers.filter((u) => u.userType === type).length;
          return (
            <button
              key={type}
              onClick={() => setTypeFilter(typeFilter === type ? null : type)}
              className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                typeFilter === type
                  ? 'bg-blueberry-500 text-white'
                  : 'bg-nightshade-100 text-nightshade-600 hover:bg-nightshade-200'
              }`}
            >
              {type.replace('_', '-')}
              <span className={`ml-1 ${typeFilter === type ? 'text-blueberry-200' : 'text-nightshade-400'}`}>
                {count}
              </span>
            </button>
          );
        })}
        <span className="mx-1 w-px h-4 bg-nightshade-200" />
        <button onClick={selectAll} className="text-xs text-blueberry-500 hover:text-blueberry-600 font-medium">
          Select all
        </button>
        {selected.size > 0 && (
          <button onClick={clearSelection} className="text-xs text-nightshade-500 hover:text-nightshade-700">
            Clear
          </button>
        )}
      </div>

      {/* User list */}
      <div className="border border-nightshade-200 rounded-lg max-h-[280px] overflow-y-auto">
        {prodUsersQuery.isLoading ? (
          <p className="text-xs text-nightshade-500 p-3">Loading production users...</p>
        ) : filtered.length === 0 ? (
          <p className="text-xs text-nightshade-500 p-3">No matching users.</p>
        ) : (
          filtered.map((u) => (
            <label
              key={u.slackUserId}
              className="flex items-center gap-3 px-3 py-2 hover:bg-nightshade-50 cursor-pointer border-b border-nightshade-100 last:border-b-0 transition-colors"
            >
              <input
                type="checkbox"
                checked={selected.has(u.slackUserId)}
                onChange={() => toggleUser(u.slackUserId)}
                className="w-3.5 h-3.5 rounded border-nightshade-300 text-blueberry-500 focus:ring-blueberry-200"
              />
              <div className="flex-1 min-w-0">
                <span className="text-sm text-nightshade-800">{u.ownerEmail ? displayNameFromEmail(u.ownerEmail) : u.slackUserId}</span>
                {u.ownerEmail && <span className="text-xs text-nightshade-500 ml-2">{u.ownerEmail}</span>}
              </div>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-nightshade-100 text-nightshade-500 font-medium shrink-0">
                {(u.userType ?? 'UNKNOWN').replace('_', '-')}
              </span>
            </label>
          ))
        )}
      </div>

      {/* Selection summary + clone button */}
      <div className="flex items-center justify-between">
        <span className="text-xs text-nightshade-600">
          {selected.size > 0
            ? `${selected.size} user${selected.size !== 1 ? 's' : ''} selected`
            : 'Select users to clone'}
        </span>
        <Button onClick={() => { void handleClone(); }} disabled={loading || selected.size === 0}>
          {loading ? 'Cloning...' : `Clone ${selected.size > 0 ? selected.size : ''} User${selected.size !== 1 ? 's' : ''}`}
        </Button>
      </div>

      {/* Result message */}
      {result && (
        <div className={`rounded-lg border px-3 py-2 text-sm ${
          result.type === 'success'
            ? 'bg-matcha-50 border-matcha-200 text-matcha-700'
            : 'bg-pomegranate-50 border-pomegranate-200 text-pomegranate-700'
        }`}>
          {result.text}
        </div>
      )}
    </div>
  );
}

type CloneScope = 'v4' | 'legacy' | 'both';

function CloneArtifactsSection({ apiFetch }: { apiFetch: ApiFetch }) {
  const queryClient = useQueryClient();
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [scope, setScope] = useState<CloneScope>('both');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Use the staging users list (same source as Seed Target Users)
  const usersQuery = useQuery<{ id: number; slackUserId: string; ownerEmail: string | null }[]>({
    queryKey: ['admin-users'],
    queryFn: async () => {
      const data = await fetchJsonOnHttp(apiFetch, AdminUsersResponseSchema, '/api/admin/users', undefined, nullOnHttp);
      return (data?.users ?? []).map((u) => ({
        id: u.id,
        slackUserId: u.slackUserId,
        ownerEmail: u.ownerEmail,
      }));
    },
  });

  const clonedUsers = useMemo(() => usersQuery.data ?? [], [usersQuery.data]);

  const handleClone = useCallback(async () => {
    if (!selectedUser) return;
    setLoading(true);
    setResult(null);
    try {
      const data = await fetchJson(apiFetch, AdminCloneArtifactsResponseSchema, '/api/admin/clone-artifacts', {
        method: 'POST',
        timeoutMs: LONG_RUNNING_MS,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slackUserId: selectedUser, scope }),
      });
      const parts: string[] = [];
      if (data.artifactCount > 0) parts.push(`${data.artifactCount} artifact${data.artifactCount !== 1 ? 's' : ''}`);
      if (data.executionCount > 0) parts.push(`${data.executionCount} execution${data.executionCount !== 1 ? 's' : ''}`);
      if (data.blueprintCount > 0) parts.push(`${data.blueprintCount} blueprint${data.blueprintCount !== 1 ? 's' : ''}`);
      if (data.legacyCount > 0) parts.push(`${data.legacyCount} legacy item${data.legacyCount !== 1 ? 's' : ''}`);
      setResult({ type: 'success', text: parts.length > 0 ? `Cloned ${parts.join(', ')}` : 'No data found to clone' });
      void queryClient.invalidateQueries({ queryKey: ['v4-artifacts'] });
    } catch (err) {
      setResult({ type: 'error', text: describeAdminFailure(err) });
    } finally {
      setLoading(false);
    }
  }, [selectedUser, scope, apiFetch, queryClient]);

  const selectedLabel = useMemo(() => {
    if (!selectedUser) return null;
    const user = clonedUsers.find((u) => u.slackUserId === selectedUser);
    return user?.ownerEmail ? displayNameFromEmail(user.ownerEmail) : selectedUser;
  }, [selectedUser, clonedUsers]);

  const scopeOptions: { value: CloneScope; label: string }[] = [
    { value: 'v4', label: 'V4' },
    { value: 'legacy', label: 'Legacy' },
    { value: 'both', label: 'Both' },
  ];

  return (
    <div className="rounded-xl border border-nightshade-200 bg-white shadow-sm p-5 space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-nightshade-800">Clone Data from Production</h3>
        <p className="text-xs text-nightshade-500 mt-0.5">
          Copy V4 artifacts, legacy items, or both from production for a cloned user.
        </p>
      </div>

      {/* User dropdown */}
      <div>
        <select
          value={selectedUser}
          onChange={(e) => { setSelectedUser(e.target.value); setResult(null); }}
          className="w-full rounded-lg border border-nightshade-200 bg-nightshade-50 px-3 py-1.5 text-sm text-nightshade-700 focus:bg-white focus:border-blueberry-300 focus:ring-2 focus:ring-blueberry-100 outline-none transition-all"
        >
          <option value="">Select a cloned user...</option>
          {clonedUsers.map((u) => (
            <option key={u.slackUserId} value={u.slackUserId}>
              {u.ownerEmail ? `${displayNameFromEmail(u.ownerEmail)} (${u.ownerEmail})` : u.slackUserId}
            </option>
          ))}
        </select>
      </div>

      {/* Scope toggle */}
      <div className="flex items-center gap-1.5">
        <span className="text-xs text-nightshade-500 mr-1">Scope:</span>
        {scopeOptions.map((opt) => (
          <button
            key={opt.value}
            onClick={() => { setScope(opt.value); setResult(null); }}
            className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
              scope === opt.value
                ? 'bg-blueberry-500 text-white'
                : 'bg-nightshade-100 text-nightshade-600 hover:bg-nightshade-200'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Clone button */}
      <div className="flex items-center justify-between">
        <span className="text-xs text-nightshade-600">
          {selectedUser ? `Selected: ${selectedLabel}` : 'Select a user to clone data'}
        </span>
        <Button onClick={() => { void handleClone(); }} disabled={loading || !selectedUser}>
          {loading ? 'Cloning...' : 'Clone Data'}
        </Button>
      </div>

      {/* Result message */}
      {result && (
        <div className={`rounded-lg border px-3 py-2 text-sm ${
          result.type === 'success'
            ? 'bg-matcha-50 border-matcha-200 text-matcha-700'
            : 'bg-pomegranate-50 border-pomegranate-200 text-pomegranate-700'
        }`}>
          {result.text}
        </div>
      )}
    </div>
  );
}

export default function AdminDemoData() {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();

  // ── Admin / user state ──
  const [selectedUserIds, setSelectedUserIds] = useState<Set<number>>(new Set());

  const meQuery = useQuery({
    queryKey: ['admin-me'],
    queryFn: () => fetchAdminMe(apiFetch),
  });

  const canSeedForOthers = meQuery.data?.ownerEmail?.toLowerCase() === 'blake.johnson@clay.com';

  const usersQuery = useQuery<AdminUser[]>({
    queryKey: ['admin-users'],
    queryFn: async () => {
      const data = await fetchJsonOnHttp(apiFetch, AdminUsersResponseSchema, '/api/admin/users', undefined, nullOnHttp);
      return (data?.users ?? []).map((u) => ({
        id: u.id,
        slackUserId: u.slackUserId,
        ownerEmail: u.ownerEmail,
      }));
    },
    enabled: canSeedForOthers,
  });

  // Loading states + progress
  const [seedAllLoading, setSeedAllLoading] = useState(false);
  const [seedProgress, setSeedProgress] = useState<any[]>([]);
  const [replayLoading, setReplayLoading] = useState(false);
  const [resettingKey, setResettingKey] = useState<ResetAction['key'] | null>(null);
  const [confirmingReset, setConfirmingReset] = useState<ResetAction['key'] | null>(null);
  const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [arcToggling, setArcToggling] = useState<string | null>(null);
  const [seedToggling, setSeedToggling] = useState<string | null>(null);
  const [arcsExpanded, setArcsExpanded] = useState(false);
  const progressRef = useRef<HTMLDivElement>(null);

  // Auto-scroll progress log to bottom
  useEffect(() => {
    if (progressRef.current) {
      progressRef.current.scrollTop = progressRef.current.scrollHeight;
    }
  }, [seedProgress]);

  // Build userIds query param for status endpoints
  const userIdsParam = canSeedForOthers && selectedUserIds.size > 0
    ? `?userIds=${[...selectedUserIds].join(',')}`
    : '';

  // Seed status query — re-fetches when selected users change
  const seedStatusQuery = useQuery<SeedStatus>({
    queryKey: ['seed-status', userIdsParam],
    queryFn: async () => {
      const empty = { blueprints: false, fill: { seeded: 0, total: 1 }, legacy: false };
      const data = await fetchJsonOnHttp(
        apiFetch,
        SeedStatusResponseSchema,
        `/api/v4/admin/blueprints/seed-status${userIdsParam}`,
        undefined,
        nullOnHttp
      );
      return data?.status ?? empty;
    },
  });

  const seedStatus = seedStatusQuery.data ?? { blueprints: false, fill: { seeded: 0, total: 1 }, legacy: false };

  // Story arcs query — re-fetches when selected users change
  const arcsQuery = useQuery<StoryArcInfo[]>({
    queryKey: ['story-arcs', userIdsParam],
    queryFn: async () => {
      const data = await fetchJsonOnHttp(
        apiFetch,
        StoryArcsListResponseSchema,
        `/api/v4/admin/blueprints/story-arcs${userIdsParam}`,
        undefined,
        nullOnHttp
      );
      return data?.arcs ?? [];
    },
  });

  /** Override apiFetch's default 15s timeout — seed operations can take minutes. */
  const noTimeout = useCallback(() => new AbortController().signal, []);

  /** Build the JSON body for seed endpoints. Includes userIds when admin has users selected. */
  const seedBody = useCallback((): RequestInit => {
    if (canSeedForOthers && selectedUserIds.size > 0) {
      return {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userIds: [...selectedUserIds] }),
        signal: noTimeout(),
      };
    }
    return { method: 'POST', signal: noTimeout() };
  }, [canSeedForOthers, selectedUserIds, noTimeout]);

  /** Build a DELETE request body, with optional userIds for multi-user support. */
  const deleteBody = useCallback((): RequestInit => {
    if (canSeedForOthers && selectedUserIds.size > 0) {
      return {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userIds: [...selectedUserIds] }),
        signal: noTimeout(),
      };
    }
    return { method: 'DELETE', signal: noTimeout() };
  }, [canSeedForOthers, selectedUserIds, noTimeout]);

  /** Build a human-readable suffix describing who data was seeded for. */
  const targetLabel = useCallback((): string => {
    if (!canSeedForOthers || selectedUserIds.size === 0) return '';
    const users = usersQuery.data ?? [];
    const emails = [...selectedUserIds]
      .map((id) => users.find((u) => u.id === id)?.ownerEmail ?? `user#${id}`)
      .slice(0, 3);
    const extra = selectedUserIds.size > 3 ? ` (+${selectedUserIds.size - 3} more)` : '';
    return ` for ${emails.join(', ')}${extra}`;
  }, [canSeedForOthers, selectedUserIds, usersQuery.data]);

  const invalidateSeedQueries = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['seed-status'] }),
      queryClient.invalidateQueries({ queryKey: ['blueprints'] }),
      queryClient.invalidateQueries({ queryKey: ['story-arcs'] }),
    ]);
  }, [queryClient]);

  const handleSeedAll = useCallback(async () => {
    setSeedAllLoading(true);
    setSeedProgress([]);
    try {
      const body: Record<string, unknown> = {};
      if (canSeedForOthers && selectedUserIds.size > 0) {
        body.userIds = [...selectedUserIds];
      }

      const init: RequestInit = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: noTimeout(),
      };

      const resp = await apiFetch('/api/v4/admin/blueprints/seed-all-stream', init);
      if (!resp.ok) throw new Error(await errorMessageFromResponse(resp, `HTTP ${resp.status}`));

      const events: any[] = [];
      // The stream ends with `complete` or `error`; an EOF without either is a failure too.
      const outcome: { completed: boolean; failure: string | null } = { completed: false, failure: null };
      await consumeSSE(resp, (event) => {
        events.push(event);
        setSeedProgress([...events]);
        if (event.step === 'complete') outcome.completed = true;
        if (event.step === 'error') {
          outcome.failure = typeof event.message === 'string' ? event.message : 'unknown error';
          // Show error events immediately
          setStatusMessage({ type: 'error', text: `Seed all failed: ${outcome.failure}` });
        }
      });

      await invalidateSeedQueries();
      if (outcome.completed && outcome.failure === null) {
        setStatusMessage({ type: 'success', text: `Seed all complete${targetLabel()}` });
      } else {
        setStatusMessage({ type: 'error', text: `Seed all failed: ${outcome.failure ?? 'the stream ended before seeding finished'}` });
      }
    } catch (err) {
      setStatusMessage({ type: 'error', text: `Seed all failed: ${(err as Error).message}` });
    } finally {
      setSeedAllLoading(false);
    }
  }, [apiFetch, canSeedForOthers, selectedUserIds, noTimeout, invalidateSeedQueries, targetLabel]);

  const handleToggleSeed = useCallback(async (category: 'demo' | 'arcs' | 'legacy', currentlyActive: boolean) => {
    setSeedToggling(category);
    try {
      if (category === 'demo') {
        if (currentlyActive) {
          const r1 = await apiFetch('/api/v4/admin/blueprints/seed-fill', deleteBody());
          if (!r1.ok) throw new Error(`Remove fill failed: ${r1.status}`);
          const r2 = await apiFetch('/api/v4/admin/blueprints/seed-concepts', deleteBody());
          if (!r2.ok) throw new Error(`Remove blueprints failed: ${r2.status}`);
        } else {
          const resp = await apiFetch('/api/v4/admin/blueprints/seed-demo-data', seedBody());
          if (!resp.ok) throw new Error(`Seed failed: ${resp.status}`);
        }
      } else if (category === 'arcs') {
        const arcIds = (arcsQuery.data ?? []).map(a => a.id);
        for (const arcId of arcIds) {
          const opts = currentlyActive ? deleteBody() : seedBody();
          const resp = await apiFetch(`/api/v4/admin/blueprints/story-arcs/${arcId}`, opts);
          if (!resp.ok) throw new Error(`${currentlyActive ? 'Remove' : 'Seed'} arc ${arcId} failed: ${resp.status}`);
        }
      } else {
        if (currentlyActive) {
          const resp = await apiFetch('/api/v4/admin/blueprints/seed-legacy', deleteBody());
          if (!resp.ok) throw new Error(`Remove legacy failed: ${resp.status}`);
        } else {
          const resp = await apiFetch('/api/v4/admin/blueprints/seed-legacy', seedBody());
          if (!resp.ok) throw new Error(`Seed legacy failed: ${resp.status}`);
        }
      }
      await invalidateSeedQueries();
    } catch (err) {
      setStatusMessage({ type: 'error', text: `Toggle ${category} failed: ${(err as Error).message}` });
    } finally {
      setSeedToggling(null);
    }
  }, [apiFetch, seedBody, deleteBody, invalidateSeedQueries, arcsQuery.data]);

  const handleReplay = useCallback(async () => {
    setReplayLoading(true);
    try {
      const data = await fetchJson(apiFetch, ReplayResponseSchema, '/api/v4/admin/blueprints/replay', {
        method: 'POST',
        timeoutMs: LONG_RUNNING_MS,
      });
      const s = data.summary;
      if (s.errors > 0) {
        const errors = data.results.filter((r) => r.status === 'error');
        logger.error({ errors }, '[replay] Error details');
        const sample = errors.slice(0, 3).map((e) => `${e.artifactType}: ${e.error}`).join('\n');
        setStatusMessage({ type: 'error', text: `Replay: ${s.created} created, ${s.errors} errors. Sample: ${sample} (full details in console)` });
      } else {
        setStatusMessage({ type: 'success', text: `Replay complete: ${s.created} created, ${s.updated} updated, ${s.skipped} skipped` });
      }
      await invalidateSeedQueries();
    } catch (err) {
      setStatusMessage({ type: 'error', text: `Replay failed: ${endedWithoutAnswer(err) ? MAY_STILL_BE_RUNNING : (err as Error).message}` });
      if (endedWithoutAnswer(err)) await invalidateSeedQueries();
    } finally {
      setReplayLoading(false);
    }
  }, [apiFetch, invalidateSeedQueries]);

  const handleResetAction = useCallback(async (action: ResetAction) => {
    setConfirmingReset(null);
    setResettingKey(action.key);
    try {
      setStatusMessage({ type: 'success', text: await action.run(apiFetch) });
      await invalidateSeedQueries();
    } catch (err) {
      setStatusMessage({ type: 'error', text: `${action.failureLabel} failed: ${describeAdminFailure(err)}` });
      if (endedWithoutAnswer(err)) await invalidateSeedQueries();
    } finally {
      setResettingKey(null);
    }
  }, [apiFetch, invalidateSeedQueries]);

  const handleToggleArc = useCallback(async (arcId: string, currentlyActive: boolean) => {
    setArcToggling(arcId);
    try {
      const opts: RequestInit = currentlyActive
        ? deleteBody()
        : seedBody();

      const resp = await apiFetch(`/api/v4/admin/blueprints/story-arcs/${arcId}`, opts);
      if (!resp.ok) throw new Error(`${currentlyActive ? 'Remove' : 'Add'} arc failed: ${resp.status}`);
      await queryClient.invalidateQueries({ queryKey: ['story-arcs'] });
    } catch (err) {
      setStatusMessage({ type: 'error', text: `Toggle arc failed: ${(err as Error).message}` });
    } finally {
      setArcToggling(null);
    }
  }, [apiFetch, queryClient, seedBody, deleteBody]);

  const isMultiUser = canSeedForOthers && selectedUserIds.size > 0;

  const seedCategories = [
    {
      key: 'demo' as const,
      label: 'Blueprints + Sample Data',
      description: '30 blueprint definitions and sample artifacts across all categories',
      active: seedStatus.blueprints || seedStatus.fill.seeded > 0,
      seeded: seedStatus.fill.seeded,
      total: seedStatus.fill.total,
      isGlobal: false,
    },
    {
      key: 'arcs' as const,
      label: 'Story Arcs',
      description: `${arcsQuery.data?.length ?? 3} narrative arcs — cross-blueprint stories for demo accounts`,
      active: (arcsQuery.data ?? []).some(a => a.active),
      // seeded/total = how many users have ALL arcs seeded
      seeded: (() => {
        const arcs = arcsQuery.data ?? [];
        if (arcs.length === 0) return 0;
        // min seeded across all arcs = users who have every arc
        return Math.min(...arcs.map(a => a.seeded));
      })(),
      total: (arcsQuery.data ?? [])[0]?.total ?? (isMultiUser ? selectedUserIds.size : 1),
      isGlobal: false,
    },
    {
      key: 'legacy' as const,
      label: 'Legacy Templates',
      description: 'Pre-V4 blueprint templates (insight, webhook, opportunity)',
      active: seedStatus.legacy,
      seeded: seedStatus.legacy ? 1 : 0,
      total: 1,
      isGlobal: true,
    },
  ];

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6 font-sans">
      <div>
        <h2 className={typography.headingLg}>Demo Data</h2>
        <p className="text-sm text-slate-500">Seed, replay, and reset demo data for the V4 view system.</p>
      </div>

      {/* User picker (blake only) */}
      {canSeedForOthers && usersQuery.data && usersQuery.data.length > 0 && (
        <div className="rounded-lg border border-indigo-200 bg-indigo-50/50 p-4 space-y-2">
          <div>
            <h3 className="text-sm font-semibold text-indigo-800">Seed Target Users</h3>
            <p className="text-xs text-indigo-600 mt-0.5">
              Select which users to create demo data for. Leave empty to seed for yourself only.
            </p>
          </div>
          <div className="max-w-sm">
            <UserMultiSelect
              users={usersQuery.data}
              selectedIds={selectedUserIds}
              onChange={setSelectedUserIds}
            />
          </div>
        </div>
      )}

      {/* Clone user from production */}
      <CloneUserSection apiFetch={apiFetch} />

      {/* Clone data from production (V4, legacy, or both) */}
      <CloneArtifactsSection apiFetch={apiFetch} />

      {/* Status message */}
      {statusMessage && (
        <div className={`rounded-lg border px-4 py-3 flex items-center justify-between ${
          statusMessage.type === 'success'
            ? 'bg-matcha-50 border-matcha-200 text-matcha-700'
            : 'bg-pomegranate-50 border-pomegranate-200 text-pomegranate-700'
        }`}>
          <span className="text-sm">{statusMessage.text}</span>
          <button onClick={() => setStatusMessage(null)} className="text-xs opacity-60 hover:opacity-100 ml-4">Dismiss</button>
        </div>
      )}

      {/* Seed Data — single card with Seed All header wrapping all toggles */}
      <div className="rounded-lg border border-slate-200 bg-white p-5 space-y-4">
        {/* Seed All header */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-slate-700">Seed Data</h3>
            <p className="text-xs text-slate-500 mt-0.5">Seeds blueprints, sample artifacts, and all story arcs. Does not run replay.</p>
          </div>
          <Button
            onClick={() => { void handleSeedAll(); }}
            disabled={seedAllLoading}
          >
            {seedAllLoading ? 'Seeding...' : isMultiUser ? `Seed All for ${selectedUserIds.size} users` : 'Seed All'}
          </Button>
        </div>

        {/* Seed progress log */}
        {seedAllLoading && seedProgress.length > 0 && (
          <div ref={progressRef} className="rounded-lg border border-slate-200 bg-slate-50 p-3 max-h-40 overflow-y-auto">
            <div className="space-y-1">
              {seedProgress.map((event, i) => (
                // eslint-disable-next-line react/no-array-index-key -- streaming append-only seed progress events, items never reorder
                <div key={i} className={`text-xs font-mono ${
                  event.status === 'done' || event.step === 'complete'
                    ? 'text-emerald-700'
                    : event.step === 'error'
                      ? 'text-red-600'
                      : 'text-slate-500'
                }`}>
                  {formatProgressEvent(event)}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Individual toggles as a list */}
        <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
          {seedCategories.map((cat) => (
            <div key={cat.key}>
              <div
                className={`flex items-center justify-between px-4 py-3 ${cat.key === 'arcs' ? 'cursor-pointer hover:bg-slate-50' : ''}`}
                onClick={cat.key === 'arcs' ? () => setArcsExpanded(!arcsExpanded) : undefined}
              >
                <div className="min-w-0 flex items-center gap-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-800">{cat.label}</span>
                      {cat.key === 'arcs' && (
                        <svg className={`w-4 h-4 text-slate-400 transition-transform ${arcsExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">{cat.description}</p>
                    {isMultiUser && !cat.isGlobal && (
                      <p className="text-xs text-indigo-600 mt-0.5">{cat.seeded}/{cat.total} users seeded</p>
                    )}
                  </div>
                </div>
                <div onClick={(e) => e.stopPropagation()}>
                  {isMultiUser ? (
                    <div className="flex gap-1.5 shrink-0">
                      {cat.seeded < cat.total && (
                        <Button
                          onClick={() => { void handleToggleSeed(cat.key, false); }}
                          disabled={seedToggling === cat.key}
                          className="text-xs"
                        >
                          {cat.isGlobal ? 'Seed' : cat.seeded === 0 ? `Seed ${cat.total}` : `Seed ${cat.total - cat.seeded}`}
                        </Button>
                      )}
                      {cat.seeded > 0 && (
                        <Button
                          variant="ghost"
                          onClick={() => { void handleToggleSeed(cat.key, true); }}
                          disabled={seedToggling === cat.key}
                          className="!text-red-600 text-xs"
                        >
                          {cat.isGlobal ? 'Remove' : `Remove ${cat.seeded}`}
                        </Button>
                      )}
                    </div>
                  ) : (
                    <ToggleSwitch
                      active={cat.active}
                      disabled={seedToggling === cat.key}
                      onClick={() => { void handleToggleSeed(cat.key, cat.active); }}
                    />
                  )}
                </div>
              </div>

              {/* Expanded arc cards */}
              {cat.key === 'arcs' && arcsExpanded && arcsQuery.data && (
                <div className="px-4 pb-4 pt-1">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {arcsQuery.data.map((arc) => (
                      <div
                        key={arc.id}
                        className={`rounded-lg border p-3 transition-all ${
                          arc.active
                            ? 'border-indigo-300 bg-white shadow-sm'
                            : 'border-slate-200 bg-slate-50/50'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-slate-800 truncate">{arc.accountName}</span>
                              <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${
                                arc.theme === 'at-risk' ? 'bg-red-100 text-red-700' :
                                arc.theme === 'expansion' ? 'bg-green-100 text-green-700' :
                                'bg-amber-100 text-amber-700'
                              }`}>
                                {arc.theme}
                              </span>
                            </div>
                            <p className="text-xs text-slate-500 mt-1 line-clamp-2">{arc.description}</p>
                            <p className="text-[10px] text-slate-400 mt-1">{arc.beatCount} artifacts across {arc.blueprintKinds.length} blueprints</p>
                            {isMultiUser && (
                              <p className="text-xs text-indigo-600 mt-1">{arc.seeded}/{arc.total} users</p>
                            )}
                          </div>
                          {isMultiUser ? (
                            <div className="flex flex-col gap-1 shrink-0">
                              {arc.seeded < arc.total && (
                                <Button
                                  onClick={() => { void handleToggleArc(arc.id, false); }}
                                  disabled={arcToggling === arc.id}
                                  className="text-xs"
                                >
                                  {arc.seeded === 0 ? `Seed ${arc.total}` : `Seed ${arc.total - arc.seeded}`}
                                </Button>
                              )}
                              {arc.seeded > 0 && (
                                <Button
                                  variant="ghost"
                                  onClick={() => { void handleToggleArc(arc.id, true); }}
                                  disabled={arcToggling === arc.id}
                                  className="!text-red-600 text-xs"
                                >
                                  Remove
                                </Button>
                              )}
                            </div>
                          ) : (
                            <ToggleSwitch
                              active={arc.active}
                              disabled={arcToggling === arc.id}
                              onClick={() => { void handleToggleArc(arc.id, arc.active); }}
                            />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Replay — outside the Seed All wrapper */}
        <div className="pt-2 border-t border-slate-100">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <span className="text-sm font-medium text-slate-800">Replay Dual-Write</span>
              <p className="text-xs text-slate-500 mt-0.5">Re-process legacy terra_ingestion rows through the V4 pipeline</p>
            </div>
            <Button variant="ghost" onClick={() => { void handleReplay(); }} disabled={replayLoading} className="shrink-0">
              {replayLoading ? 'Replaying...' : 'Replay'}
            </Button>
          </div>
        </div>
      </div>

      {/* Section 3: Danger Zone */}
      <div className="space-y-4">
        {/* Backfill — separate danger card */}
        <BackfillSection apiFetch={apiFetch} />

        {/* Destructive resets */}
        <div className="rounded-lg border border-red-200 bg-red-50/50 p-5 space-y-4">
          <div>
            <h3 className="text-sm font-semibold text-red-700">Danger Zone</h3>
            <p className="text-xs text-red-500 mt-0.5">Destructive actions that permanently delete data for <strong>all users</strong>.</p>
          </div>
        <div className="space-y-3">
          {resetActions.map((action) => (
            <DestructiveActionRow
              key={action.key}
              action={action}
              isConfirming={confirmingReset === action.key}
              isResetting={resettingKey === action.key}
              onRequestConfirm={() => setConfirmingReset(action.key)}
              onCancel={() => setConfirmingReset(null)}
              onConfirm={() => { void handleResetAction(action); }}
            />
          ))}
        </div>
      </div>
      </div>
    </div>
  );
}
